// ============================================================
// 📁 آدرس این فایل در پروژه:
//    src/pages/finance/journal/Expense.tsx
// 
// این فایل را در همان مسیر بالا کپی کنید (روی فایل قبلی بنویسید)
// ============================================================

import { useState, useEffect, useCallback } from "react";
import { useTheme, DARK, LIGHT } from "../../../components/Layout";
import { journalAPI, accountsAPI } from "../../../services/api";
import { usePermission } from "../../../hooks/usePermission";
import { useSubmit } from "../../../hooks/useSubmit";
import SearchableSelect from "../../../components/SearchableSelect";
import PersianDatePicker, { todayGregorian, gregorianToJalaliStr, DualDate } from "../../../utils/PersianDatePicker";
import { Plus, Trash2, Edit2, Settings, X, Check } from "lucide-react";
import JournalFilter, { emptyFilter, applyFilter, JournalFilterValues, exportToExcel } from "./JournalFilter";
import {
  StatusTabs, PendingBadge, ConfirmIconButton, RevertIconButton,
  PendingSubmitButton, ConfirmSubmitButton, PendingHint, pendingRowStyle,
} from "./PendingControls";
import {
  inputSt, rowSt, badgeSt, addBtnSt, iconBtnSt,
  Lbl, BtnCancel, BtnSave, Toast, Loading, Empty, Modal,
} from "./Purchase";

const fmt = (n: any) =>
  new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n) || 0);

const CURRENCIES = ["USD", "AFN", "IRR", "TRY", "PKR", "EUR"];

export default function Expense() {
  const { dark } = useTheme();
  const T = dark ? DARK : LIGHT;
  const canAdd    = usePermission("finance", "add");
  const canEdit   = usePermission("finance", "edit");
  const canDelete = usePermission("finance", "delete");
  const { submitting, submit } = useSubmit();

  const [list, setList]             = useState<any[]>([]);
  const [loading, setLoading]       = useState(true);
  const [showForm, setShowForm]     = useState(false);
  const [editItem, setEditItem]     = useState<any>(null);
  const [showCats, setShowCats]     = useState(false);
  const [categories, setCategories] = useState<any[]>([]);
  const [cashAccs, setCashAccs]     = useState<any[]>([]);
  const [filter, setFilter]         = useState<JournalFilterValues>(emptyFilter);
  const [toast, setToast]           = useState({ text: "", ok: true });
  const [payModal, setPayModal]     = useState<any>(null);
  const [payAccId, setPayAccId]     = useState("");
  const [catForm, setCatForm]       = useState({ name: "", parent_id: "" });
  const [editCat, setEditCat]       = useState<any>(null);
  const [statusFilter, setStatusFilter] = useState<"all"|"pending"|"paid">("all");

  const [form, setForm] = useState({
    date: todayGregorian(), recipient_name: "", category_id: "", category_name: "",
    currency: "USD", amount: "", exchange_rate: "1", status: "pending",
    account_id: "", account_name: "", notes: "", attachment: "",
  });

  const load = useCallback(async () => {
    setLoading(true);
    const [e, c, a] = await Promise.all([
      journalAPI.listExpenses(), journalAPI.getExpenseCats(), accountsAPI.getCashAccounts(),
    ]);
    if (e.success) setList(e.expenses || []);
    if (c.success) setCategories(c.categories || []);
    if (a.success) setCashAccs(a.accounts || []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const showToast = (text: string, ok = true) => {
    setToast({ text, ok });
    setTimeout(() => setToast({ text: "", ok: true }), 3000);
  };

  const catOptions = (cats: any[]) => {
    const opts: { value: string; label: string }[] = [{ value: "", label: "— بدون دسته‌بندی —" }];
    cats.filter(c => !c.parent_id).forEach((p: any) => {
      opts.push({ value: String(p.id), label: p.name });
      cats.filter(c => c.parent_id === p.id).forEach((ch: any) => {
        opts.push({ value: String(ch.id), label: `    └ ${ch.name}` });
      });
    });
    return opts;
  };

  const buildSubmitPayload = (statusValue: "pending"|"paid") => {
    const cat = categories.find((c: any) => String(c.id) === form.category_id);
    const account = cashAccs.find((a: any) => String(a.id) === form.account_id);
    return {
      date: form.date, recipient_name: form.recipient_name,
      category_id: form.category_id ? parseInt(form.category_id) : null,
      category_name: cat?.name || "", currency: form.currency,
      amount: parseFloat(form.amount), exchange_rate: parseFloat(form.exchange_rate || "1"),
      status: statusValue,
      account_id: form.account_id ? parseInt(form.account_id) : null,
      account_name: account?.name || "", notes: form.notes, attachment: form.attachment,
    };
  };

  const validateForm = (statusValue: "pending"|"paid") => {
    if (!form.recipient_name) { showToast("نام دریافت‌کننده الزامی است", false); return false; }
    if (!form.amount) { showToast("مبلغ الزامی است", false); return false; }
    if (statusValue === "paid" && !form.account_id) { showToast("حساب پرداختی برای ثبت نهایی الزامی است", false); return false; }
    return true;
  };

  const resetForm = () => setForm({ date: todayGregorian(), recipient_name: "", category_id: "", category_name: "", currency: "USD", amount: "", exchange_rate: "1", status: "pending", account_id: "", account_name: "", notes: "", attachment: "" });

  const handleSubmit = async () => {
    if (!validateForm("paid")) return;
    const res = await journalAPI.addExpense(buildSubmitPayload("paid"));
    if (res.success) {
      showToast(`مصرف ${res.serial} ثبت شد`);
      setShowForm(false); resetForm(); load();
    } else showToast(res.message || "خطا", false);
  };

  const handleSubmitPending = async () => {
    if (!validateForm("pending")) return;
    const res = await journalAPI.addExpense(buildSubmitPayload("pending"));
    if (res.success) {
      showToast(`مصرف ${res.serial} در انتظار تأیید ذخیره شد`);
      setShowForm(false); resetForm(); load();
    } else showToast(res.message || "خطا", false);
  };

  const handleConfirm = async (item: any) => {
    if (!item.account_id) {
      // اگر حساب ندارد، باید مودال انتخاب حساب باز شود
      setPayModal(item);
      return;
    }
    if (!window.confirm(`تأیید نهایی مصرف ${item.serial}؟\nمبلغ از حساب کم می‌شود.`)) return;
    const res = await journalAPI.confirmExpense(item.id);
    if (res.success) { showToast("مصرف تأیید شد"); load(); }
    else showToast(res.message || "خطا", false);
  };

  const handleRevert = async (id: number, serial: string) => {
    if (!window.confirm(`برگرداندن مصرف ${serial} به حالت در انتظار؟\nمبلغ به حساب برگردانده می‌شود.`)) return;
    const res = await journalAPI.revertExpense(id);
    if (res.success) { showToast("به حالت در انتظار برگشت"); load(); }
    else showToast(res.message || "خطا", false);
  };

  const openEdit = (e: any) => setEditItem({
    id: e.id, date: e.date, recipient_name: e.recipient_name,
    category_id: e.category_id ? String(e.category_id) : "",
    category_name: e.category_name || "",
    currency: e.currency, amount: String(e.amount),
    exchange_rate: String(e.exchange_rate || "1"),
    status: e.status,
    account_id: e.account_id ? String(e.account_id) : "",
    account_name: e.account_name || "",
    notes: e.notes || "", attachment: e.attachment || "",
  });

  const handleEdit = async () => {
    if (!editItem.recipient_name) { showToast("نام دریافت‌کننده الزامی است", false); return; }
    if (!editItem.amount) { showToast("مبلغ الزامی است", false); return; }
    if (editItem.status === "paid" && !editItem.account_id) { showToast("حساب پرداختی الزامی است", false); return; }
    const cat = categories.find((c: any) => String(c.id) === editItem.category_id);
    const account = cashAccs.find((a: any) => String(a.id) === editItem.account_id);
    const res = await journalAPI.updateExpense({
      id: editItem.id, date: editItem.date, recipient_name: editItem.recipient_name,
      category_id: editItem.category_id ? parseInt(editItem.category_id) : null,
      category_name: cat?.name || editItem.category_name,
      currency: editItem.currency, amount: parseFloat(editItem.amount),
      exchange_rate: parseFloat(editItem.exchange_rate || "1"),
      status: editItem.status,
      account_id: editItem.account_id ? parseInt(editItem.account_id) : null,
      account_name: account?.name || editItem.account_name,
      notes: editItem.notes, attachment: editItem.attachment,
    });
    if (res.success) { showToast("مصرف ویرایش شد"); setEditItem(null); load(); }
    else showToast(res.message || "خطا", false);
  };

  const handlePay = async () => {
    if (!payAccId) { showToast("حساب الزامی است", false); return; }
    const account = cashAccs.find((a: any) => String(a.id) === payAccId);
    const res = await journalAPI.payExpense(payModal.id, parseInt(payAccId), account?.name || "");
    if (res.success) { showToast("مصرف پرداخت شد"); setPayModal(null); setPayAccId(""); load(); }
    else showToast(res.message || "خطا", false);
  };

  const handleDelete = async (id: number, serial: string) => {
    if (!window.confirm(`حذف مصرف ${serial}؟`)) return;
    const res = await journalAPI.deleteExpense(id);
    if (res.success) { showToast("مصرف حذف شد"); load(); }
    else showToast(res.message || "خطا", false);
  };

  const handleAddCat = async () => {
    if (!catForm.name) { showToast("نام الزامی است", false); return; }
    const res = await journalAPI.addExpenseCat({ name: catForm.name, parent_id: catForm.parent_id ? parseInt(catForm.parent_id) : null });
    if (res.success) { setCatForm({ name: "", parent_id: "" }); load(); }
    else showToast(res.message || "خطا", false);
  };

  const handleEditCat = async (id: number, name: string) => {
    const res = await journalAPI.updateExpenseCat({ id, name });
    if (res.success) { setEditCat(null); load(); }
    else showToast(res.message || "خطا", false);
  };

  const handleDeleteCat = async (id: number) => {
    if (!window.confirm("حذف دسته‌بندی؟")) return;
    const res = await journalAPI.deleteExpenseCat(id);
    if (res.success) load();
    else showToast(res.message || "خطا", false);
  };

  const filtered = applyFilter(list, filter, {
    searchFields: ["serial", "recipient_name", "notes", "category_name"],
    dateField: "date",
    amountField: "amount",
    currencyField: "currency",
    statusField: "status",
    categoryField: "category_id",
    accountField: "account_id",
  }).filter((e: any) => {
    const st = e.status || "paid";
    if (statusFilter === "all") return true;
    return st === statusFilter;
  });

  const pendingCount = list.filter((e:any) => (e.status||"paid") === "pending").length;

  const editUsdAmt = editItem ? (parseFloat(editItem.amount || "0") / parseFloat(editItem.exchange_rate || "1")) : 0;

  return (
    <div style={{ fontFamily: "'Vazirmatn','Segoe UI',sans-serif" }}>
      {toast.text && <Toast text={toast.text} ok={toast.ok} />}

      <JournalFilter
        config={{
          searchPlaceholder: "جستجو در مصارف... (شماره سند، نام دریافت‌کننده، دسته‌بندی)",
          showDateRange: true,
          showAmountRange: true,
          showCurrency: true,
          showStatus: true,
          statusOptions: [
            { value: "paid", label: "پرداخت شده" },
            { value: "pending", label: "در انتظار" },
          ],
          showCategory: true,
          categoryOptions: catOptions(categories),
          showAccount: true,
          accountOptions: cashAccs.map((a: any) => ({ value: String(a.id), label: a.name })),
        }}
        values={filter}
        onChange={setFilter}
        resultCount={filtered.length}
        totalCount={list.length}
        T={T}
        dark={dark}
            onExport={() => exportToExcel(filtered.map(e => ({
          "شماره سند": e.serial,
          "تاریخ": e.date,
          "دریافت‌کننده": e.recipient_name,
          "دسته‌بندی": e.category_name || "",
          "مبلغ": e.amount,
          "ارز": e.currency,
          "نرخ تبدیل": e.exchange_rate,
          "معادل دالر": e.usd_amount,
          "وضعیت": e.status,
          "حساب": e.account_name || "",
          "توضیحات": e.notes || "",
        })), "مصارف")}
      exportLabel="دانلود Excel مصارف"
      />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, gap: 10, flexWrap: "wrap" }}>
        <StatusTabs
          value={statusFilter}
          onChange={setStatusFilter}
          totalCount={list.length}
          pendingCount={pendingCount}
          T={T}
          dark={dark}
          confirmedValue="paid"
        />
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={() => setShowCats(true)} style={{ display: "flex", alignItems: "center", gap: 6, padding: "10px 16px", borderRadius: 10, border: `1.5px solid ${T.cardBorder}`, background: "transparent", color: T.text, cursor: "pointer", fontSize: 13, fontWeight: 700, fontFamily: "'Vazirmatn','Segoe UI',sans-serif" }}>
            <Settings size={14} /> دسته‌بندی‌ها
          </button>
          {canAdd && <button onClick={() => setShowForm(true)} style={addBtnSt("#ef4444")}><Plus size={15} /> ثبت مصرف جدید</button>}
        </div>
      </div>

      {loading ? <Loading T={T} /> : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {filtered.length === 0 && <Empty T={T} text="هیچ مصرفی ثبت نشده" />}
          {/* ردیف هدر */}
          {filtered.length > 0 && (
            <div style={{
              display: "grid",
              gridTemplateColumns: "130px 1fr 110px 1fr 1.2fr 180px 180px",
              gap: 12,
              padding: "10px 16px",
              borderRadius: 10,
              background: dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)",
              border: `1px solid ${T.cardBorder}`,
              fontSize: 11,
              fontWeight: 700,
              color: T.subtext,
            }}>
              <div>شماره سند</div>
              <div>دریافت‌کننده</div>
              <div>تاریخ</div>
              <div>دسته‌بندی</div>
              <div>شرح</div>
              <div style={{ textAlign: "left" as const }}>مبلغ</div>
              <div style={{ textAlign: "center" as const }}>عملیات</div>
            </div>
          )}
          {filtered.map((e: any) => {
            const isPending = (e.status || "paid") === "pending";
            return (
            <div key={e.id} style={{
              ...rowSt(dark, T),
              ...(isPending ? pendingRowStyle(dark) : {}),
              display: "grid",
              gridTemplateColumns: "130px 1fr 110px 1fr 1.2fr 180px 180px",
              gap: 12,
              alignItems: "center",
            }}>
              {/* ستون 1: سریال + بدج */}
              <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-start" }}>
                <span style={badgeSt("#ef4444")}>{e.serial}</span>
                {isPending && <PendingBadge />}
              </div>

              {/* ستون 2: نام دریافت‌کننده */}
              <div style={{ fontWeight: 700, color: T.text, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis" }}>
                {e.recipient_name}
              </div>

              {/* ستون 3: تاریخ */}
              <div>
                <DualDate date={e.date} fontSize={12} color={T.subtext} />
              </div>

              {/* ستون 4: دسته‌بندی */}
              <div>
                {e.category_name && <span style={{ fontSize: 11, color: T.subtext, background: dark ? "rgba(255,255,255,0.06)" : "#f1f5f9", padding: "2px 8px", borderRadius: 6, whiteSpace: "nowrap" as const }}>{e.category_name}</span>}
              </div>

              {/* ستون 5: شرح */}
              <div style={{ fontSize: 11, color: T.subtext, fontStyle: "italic", lineHeight: 1.5, wordBreak: "break-word" as const }}>
                {e.notes || "—"}
              </div>

              {/* ستون 6: مبلغ */}
              <div style={{ display: "flex", flexDirection: "column", gap: 2, alignItems: "flex-end" }}>
                <span style={{ fontWeight: 800, color: "#ef4444", fontSize: 14, direction: "ltr" as const }}>{fmt(e.amount)} {e.currency}</span>
                <span style={{ fontSize: 11, color: T.subtext, direction: "ltr" as const }}>≈ ${fmt(e.usd_amount)}</span>
              </div>

              {/* ستون 7: عملیات */}
              <div style={{ display: "flex", gap: 6, alignItems: "center", justifyContent: "flex-end" }}>
                {isPending && canEdit && (
                  <ConfirmIconButton onClick={() => handleConfirm(e)} />
                )}
                {!isPending && canEdit && (
                  <RevertIconButton onClick={() => handleRevert(e.id, e.serial)} />
                )}
                {canEdit && <button onClick={() => openEdit(e)} title="ویرایش" style={iconBtnSt("#3b82f6")}><Edit2 size={13} /></button>}
                {canDelete && <button onClick={() => handleDelete(e.id, e.serial)} title="حذف" style={iconBtnSt("#ef4444")}><Trash2 size={13} /></button>}
              </div>
            </div>
            );
          })}
        </div>
      )}

      {/* فرم ثبت */}
      {showForm && (
        <Modal title="ثبت مصرف جدید" onClose={() => setShowForm(false)} dark={dark} T={T}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <div><Lbl T={T}>نام دریافت‌کننده *</Lbl><input value={form.recipient_name} onChange={e => setForm(f => ({ ...f, recipient_name: e.target.value }))} placeholder="نام شخص دریافت‌کننده" style={inputSt(T)} /></div>
            <div><Lbl T={T}>تاریخ</Lbl><PersianDatePicker value={form.date} onChange={v => setForm(f => ({ ...f, date: v }))} /></div>
            <div style={{ gridColumn: "1/-1" }}>
              <Lbl T={T}>دسته‌بندی مصرف</Lbl>
              <SearchableSelect options={catOptions(categories)} value={form.category_id} onChange={v => { const cat = categories.find((c: any) => String(c.id) === v); setForm(f => ({ ...f, category_id: v, category_name: cat?.name || "" })); }} placeholder="انتخاب دسته‌بندی" />
            </div>
            <div><Lbl T={T}>ارز مصرفی</Lbl><SearchableSelect options={CURRENCIES.map(c => ({ value: c, label: c }))} value={form.currency} onChange={v => setForm(f => ({ ...f, currency: v }))} placeholder="انتخاب ارز" /></div>
            <div><Lbl T={T}>مبلغ *</Lbl><input type="number" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} placeholder="0.00" style={inputSt(T)} /></div>
            <div><Lbl T={T}>نرخ تبادله به دالر</Lbl><input type="number" value={form.exchange_rate} onChange={e => setForm(f => ({ ...f, exchange_rate: e.target.value }))} placeholder="1" style={inputSt(T)} /></div>
            <div>
              <Lbl T={T}>معادل به دالر</Lbl>
              <div style={{ padding: "10px 14px", borderRadius: 10, background: dark ? "rgba(239,68,68,0.1)" : "#fef2f2", color: "#ef4444", fontWeight: 700, fontSize: 14, border: "1.5px solid #ef444420" }}>${fmt(parseFloat(form.amount || "0") / parseFloat(form.exchange_rate || "1"))}</div>
            </div>
            <div style={{ gridColumn: "1/-1" }}>
              <Lbl T={T}>حساب پرداختی <span style={{color:"#94a3b8",fontSize:10}}>(برای ثبت نهایی الزامی)</span></Lbl>
              <SearchableSelect options={cashAccs.map((a: any) => ({ value: String(a.id), label: `${a.code} — ${a.name} (${a.currency}) | موجودی: ${fmt(a.balance)}` }))} value={form.account_id}
                onChange={v => { const ac = cashAccs.find((x: any) => String(x.id) === v); setForm(f => ({ ...f, account_id: v, account_name: ac?.name || "" })); }} placeholder="انتخاب حساب" />
            </div>
            <div><Lbl T={T}>توضیحات</Lbl><input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="توضیحات..." style={inputSt(T)} /></div>
            <div><Lbl T={T}>ضمیمه</Lbl><input value={form.attachment} onChange={e => setForm(f => ({ ...f, attachment: e.target.value }))} placeholder="لینک..." style={inputSt(T)} /></div>
          </div>
          <div style={{
            display: "flex", gap: 12, justifyContent: "space-between", alignItems: "center",
            marginTop: 24, paddingTop: 20, borderTop: `1px solid ${T.cardBorder}`,
            flexWrap: "wrap"
          }}>
            <PendingHint T={T} />
            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <BtnCancel onClick={() => setShowForm(false)} T={T} />
              <PendingSubmitButton
                onClick={() => submit(handleSubmitPending)}
                loading={submitting}
                label="ذخیره مصرف در انتظار"
              />
              <ConfirmSubmitButton
                onClick={() => submit(handleSubmit)}
                loading={submitting}
                color="#ef4444"
                label="ثبت مصرف"
              />
            </div>
          </div>
        </Modal>
      )}

      {/* فرم ویرایش */}
      {editItem && (
        <Modal title="ویرایش مصرف" onClose={() => setEditItem(null)} dark={dark} T={T}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <div><Lbl T={T}>نام دریافت‌کننده *</Lbl><input value={editItem.recipient_name} onChange={e => setEditItem((f: any) => ({ ...f, recipient_name: e.target.value }))} placeholder="نام شخص" style={inputSt(T)} /></div>
            <div><Lbl T={T}>تاریخ</Lbl><PersianDatePicker value={editItem.date} onChange={v => setEditItem((f: any) => ({ ...f, date: v }))} /></div>
            <div style={{ gridColumn: "1/-1" }}>
              <Lbl T={T}>دسته‌بندی مصرف</Lbl>
              <SearchableSelect options={catOptions(categories)} value={editItem.category_id} onChange={v => { const cat = categories.find((c: any) => String(c.id) === v); setEditItem((f: any) => ({ ...f, category_id: v, category_name: cat?.name || "" })); }} placeholder="انتخاب دسته‌بندی" />
            </div>
            <div><Lbl T={T}>ارز مصرفی</Lbl><SearchableSelect options={CURRENCIES.map(c => ({ value: c, label: c }))} value={editItem.currency} onChange={v => setEditItem((f: any) => ({ ...f, currency: v }))} placeholder="انتخاب ارز" /></div>
            <div><Lbl T={T}>مبلغ *</Lbl><input type="number" value={editItem.amount} onChange={e => setEditItem((f: any) => ({ ...f, amount: e.target.value }))} placeholder="0.00" style={inputSt(T)} /></div>
            <div><Lbl T={T}>نرخ تبادله به دالر</Lbl><input type="number" value={editItem.exchange_rate} onChange={e => setEditItem((f: any) => ({ ...f, exchange_rate: e.target.value }))} placeholder="1" style={inputSt(T)} /></div>
            <div>
              <Lbl T={T}>معادل به دالر</Lbl>
              <div style={{ padding: "10px 14px", borderRadius: 10, background: dark ? "rgba(239,68,68,0.1)" : "#fef2f2", color: "#ef4444", fontWeight: 700, fontSize: 14, border: "1.5px solid #ef444420" }}>${fmt(editUsdAmt)}</div>
            </div>
            <div style={{ gridColumn: "1/-1" }}>
              <Lbl T={T}>وضعیت پرداخت</Lbl>
              <div style={{ display: "flex", gap: 10 }}>
                {[{ v: "pending", l: "در انتظار", c: "#f59e0b" }, { v: "paid", l: "پرداخت شده", c: "#10b981" }].map(opt => (
                  <button key={opt.v} onClick={() => setEditItem((f: any) => ({ ...f, status: opt.v, account_id: opt.v === "pending" ? "" : f.account_id }))}
                    style={{ flex: 1, padding: "10px", borderRadius: 10, border: "2px solid", borderColor: editItem.status === opt.v ? opt.c : T.cardBorder, background: editItem.status === opt.v ? `${opt.c}18` : "transparent", color: editItem.status === opt.v ? opt.c : T.subtext, cursor: "pointer", fontWeight: 700, fontSize: 13, fontFamily: "'Vazirmatn','Segoe UI',sans-serif" }}>
                    {opt.l}
                  </button>
                ))}
              </div>
            </div>
            {editItem.status === "paid" && (
              <div style={{ gridColumn: "1/-1" }}>
                <Lbl T={T}>حساب پرداختی *</Lbl>
                <SearchableSelect options={cashAccs.map((a: any) => ({ value: String(a.id), label: `${a.code} — ${a.name} (${a.currency}) | موجودی: ${fmt(a.balance)}` }))} value={editItem.account_id}
                  onChange={v => { const ac = cashAccs.find((x: any) => String(x.id) === v); setEditItem((f: any) => ({ ...f, account_id: v, account_name: ac?.name || "" })); }} placeholder="انتخاب حساب" />
              </div>
            )}
            <div><Lbl T={T}>توضیحات</Lbl><input value={editItem.notes} onChange={e => setEditItem((f: any) => ({ ...f, notes: e.target.value }))} placeholder="توضیحات..." style={inputSt(T)} /></div>
            <div><Lbl T={T}>ضمیمه</Lbl><input value={editItem.attachment} onChange={e => setEditItem((f: any) => ({ ...f, attachment: e.target.value }))} placeholder="لینک..." style={inputSt(T)} /></div>
          </div>
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 20 }}>
            <BtnCancel onClick={() => setEditItem(null)} T={T} />
            <BtnSave onClick={() => submit(handleEdit)} loading={submitting} color="#ef4444" label="ذخیره ویرایش" />
          </div>
        </Modal>
      )}

      {/* پرداخت مصرف در انتظار */}
      {payModal && (
        <Modal title={`پرداخت مصرف: ${payModal.serial}`} onClose={() => setPayModal(null)} dark={dark} T={T}>
          <div style={{ marginBottom: 16, padding: "14px 18px", borderRadius: 12, background: dark ? "rgba(239,68,68,0.08)" : "#fef2f2" }}>
            <div style={{ fontSize: 13, color: T.text, marginBottom: 6 }}>دریافت‌کننده: <strong>{payModal.recipient_name}</strong></div>
            <div style={{ fontSize: 13, color: T.text }}>مبلغ: <strong style={{ color: "#ef4444" }}>{fmt(payModal.amount)} {payModal.currency}</strong></div>
          </div>
          <Lbl T={T}>حساب پرداختی *</Lbl>
          <SearchableSelect options={cashAccs.map((a: any) => ({ value: String(a.id), label: `${a.code} — ${a.name} (${a.currency}) | موجودی: ${fmt(a.balance)}` }))} value={payAccId} onChange={setPayAccId} placeholder="انتخاب حساب" />
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 20 }}>
            <BtnCancel onClick={() => setPayModal(null)} T={T} />
            <BtnSave onClick={() => submit(handlePay)} loading={submitting} color="#10b981" label="تأیید پرداخت" />
          </div>
        </Modal>
      )}

      {/* مدیریت دسته‌بندی */}
      {showCats && (
        <Modal title="مدیریت دسته‌بندی مصارف" onClose={() => setShowCats(false)} dark={dark} T={T}>
          <div style={{ display: "flex", gap: 10, marginBottom: 20, alignItems: "flex-end" }}>
            <div style={{ flex: 1 }}><Lbl T={T}>نام دسته‌بندی</Lbl><input value={catForm.name} onChange={e => setCatForm(f => ({ ...f, name: e.target.value }))} placeholder="نام دسته‌بندی..." style={inputSt(T)} /></div>
            <div style={{ flex: 1 }}>
              <Lbl T={T}>زیرمجموعه (اختیاری)</Lbl>
              <SearchableSelect options={[{ value: "", label: "— دسته اصلی —" }, ...categories.filter((c: any) => !c.parent_id).map((c: any) => ({ value: String(c.id), label: c.name }))]} value={catForm.parent_id} onChange={v => setCatForm(f => ({ ...f, parent_id: v }))} placeholder="انتخاب مادر" />
            </div>
            <button onClick={handleAddCat} style={addBtnSt("#ef4444")}><Plus size={14} /> افزودن</button>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {categories.filter((c: any) => !c.parent_id).map((cat: any) => (
              <div key={cat.id}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", borderRadius: 10, background: dark ? "rgba(255,255,255,0.04)" : "#f8fafc", border: `1px solid ${T.cardBorder}` }}>
                  {editCat?.id === cat.id ? (
                    <><input value={editCat.name} onChange={e => setEditCat((c: any) => ({ ...c, name: e.target.value }))} style={{ ...inputSt(T), flex: 1 }} /><button onClick={() => handleEditCat(cat.id, editCat.name)} style={iconBtnSt("#10b981")}><Check size={13} /></button><button onClick={() => setEditCat(null)} style={iconBtnSt("#6b7280")}><X size={13} /></button></>
                  ) : (
                    <><span style={{ fontWeight: 700, color: T.text, flex: 1 }}>{cat.name}</span>{canEdit && <button onClick={() => setEditCat({ id: cat.id, name: cat.name })} style={iconBtnSt("#3b82f6")}><Edit2 size={13} /></button>}{canDelete && <button onClick={() => handleDeleteCat(cat.id)} style={iconBtnSt("#ef4444")}><Trash2 size={13} /></button>}</>
                  )}
                </div>
                {categories.filter((c: any) => c.parent_id === cat.id).map((child: any) => (
                  <div key={child.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 14px 8px 36px", borderRadius: 8, marginTop: 4, background: dark ? "rgba(255,255,255,0.02)" : "#fff", border: `1px solid ${T.cardBorder}` }}>
                    <span style={{ color: "#6b7280" }}>└</span>
                    {editCat?.id === child.id ? (
                      <><input value={editCat.name} onChange={e => setEditCat((c: any) => ({ ...c, name: e.target.value }))} style={{ ...inputSt(T), flex: 1 }} /><button onClick={() => handleEditCat(child.id, editCat.name)} style={iconBtnSt("#10b981")}><Check size={13} /></button><button onClick={() => setEditCat(null)} style={iconBtnSt("#6b7280")}><X size={13} /></button></>
                    ) : (
                      <><span style={{ color: T.text, flex: 1 }}>{child.name}</span>{canEdit && <button onClick={() => setEditCat({ id: child.id, name: child.name })} style={iconBtnSt("#3b82f6")}><Edit2 size={13} /></button>}{canDelete && <button onClick={() => handleDeleteCat(child.id)} style={iconBtnSt("#ef4444")}><Trash2 size={13} /></button>}</>
                    )}
                  </div>
                ))}
              </div>
            ))}
            {categories.length === 0 && <Empty T={T} text="هیچ دسته‌بندی‌ای ایجاد نشده" />}
          </div>
        </Modal>
      )}
    </div>
  );
}